import React, { useState, useEffect } from 'react';
import { 
  Plus, 
  Users, 
  Zap, 
  Wrench,
  Calculator,
  Trash2,
  Edit,
  DollarSign,
  Calendar,
  TrendingUp,
  Fuel
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { 
  Dialog, 
  DialogContent, 
  DialogHeader, 
  DialogTitle, 
  DialogTrigger,
  DialogFooter,
  DialogClose,
  DialogDescription
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '../components/ui/alert-dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { operationalCostsAPI } from '../lib/api';
import { formatCurrency, formatDate } from '../lib/utils';
import { toast } from 'sonner';

const EQUIPMENT_TYPES = [
  "Motor Eléctrico",
  "Mezcladora",
  "Molino",
  "Transportador",
  "Elevador",
  "Silo",
  "Báscula",
  "Compresor",
  "Bomba",
  "Panel Eléctrico",
  "Otro"
];

const POSITIONS = [
  "Operador de Producción",
  "Supervisor",
  "Técnico de Mantenimiento",
  "Almacenista",
  "Chofer",
  "Auxiliar",
  "Administrador",
  "Gerente"
];

export default function OperationalCostsPage() {
  const [employees, setEmployees] = useState([]);
  const [payroll, setPayroll] = useState([]);
  const [electricityExpenses, setElectricityExpenses] = useState([]);
  const [maintenanceExpenses, setMaintenanceExpenses] = useState([]);
  const [gasoilExpenses, setGasoilExpenses] = useState([]);
  const [costSummary, setCostSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('summary');
  
  // Current period for filtering
  const currentDate = new Date();
  const [selectedPeriod, setSelectedPeriod] = useState(
    `${currentDate.getFullYear()}-${String(currentDate.getMonth() + 1).padStart(2, '0')}`
  );
  
  // Form states
  const [employeeForm, setEmployeeForm] = useState({
    name: '', position: '', salary: '', hire_date: '', status: 'activo', notes: ''
  });
  const [payrollForm, setPayrollForm] = useState({
    employee_id: '', period: selectedPeriod, amount: '', payment_date: '', notes: ''
  });
  const [electricityForm, setElectricityForm] = useState({
    period: selectedPeriod, amount: '', kwh: '', invoice_number: '', payment_date: '', notes: ''
  });
  const [maintenanceForm, setMaintenanceForm] = useState({
    description: '', equipment_type: '', amount: '', expense_date: '', technician: '', notes: ''
  });
  const [gasoilForm, setGasoilForm] = useState({
    liters: '', price_per_liter: '', expense_date: '', supplier: '', invoice_number: '', notes: ''
  });
  
  const [editingEmployee, setEditingEmployee] = useState(null);
  const [editingElectricity, setEditingElectricity] = useState(null);
  const [editingMaintenance, setEditingMaintenance] = useState(null);
  const [editingGasoil, setEditingGasoil] = useState(null);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [empRes, payRes, elecRes, maintRes, gasoilRes] = await Promise.all([
        operationalCostsAPI.getEmployees(),
        operationalCostsAPI.getPayroll(selectedPeriod),
        operationalCostsAPI.getElectricityExpenses(selectedPeriod),
        operationalCostsAPI.getMaintenanceExpenses(selectedPeriod),
        operationalCostsAPI.getGasoilExpenses(selectedPeriod),
      ]);
      setEmployees(empRes.data);
      setPayroll(payRes.data);
      setElectricityExpenses(elecRes.data);
      setMaintenanceExpenses(maintRes.data);
      setGasoilExpenses(gasoilRes.data);
      
      // Fetch cost summary
      try {
        const summaryRes = await operationalCostsAPI.getCostSummary(selectedPeriod);
        setCostSummary(summaryRes.data);
      } catch (err) {
        setCostSummary(null);
      }
    } catch (error) {
      toast.error('Error al cargar datos');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [selectedPeriod]);

  // Generate period options (last 12 months)
  const periodOptions = [];
  for (let i = 0; i < 12; i++) {
    const d = new Date(currentDate.getFullYear(), currentDate.getMonth() - i, 1);
    const period = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
    const label = d.toLocaleDateString('es-DO', { year: 'numeric', month: 'long' });
    periodOptions.push({ value: period, label });
  }

  // === EMPLOYEE HANDLERS ===
  const resetEmployeeForm = () => {
    setEmployeeForm({ name: '', position: '', salary: '', hire_date: '', status: 'activo', notes: '' });
    setEditingEmployee(null);
  };

  const handleSaveEmployee = async (e) => {
    e.preventDefault();
    try {
      if (editingEmployee) {
        await operationalCostsAPI.updateEmployee(editingEmployee.id, {
          ...employeeForm,
          salary: parseFloat(employeeForm.salary)
        });
        toast.success('Empleado actualizado');
      } else {
        await operationalCostsAPI.createEmployee({
          ...employeeForm,
          salary: parseFloat(employeeForm.salary)
        });
        toast.success('Empleado registrado');
      }
      resetEmployeeForm();
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al guardar');
    }
  };

  const handleEditEmployee = (emp) => {
    setEditingEmployee(emp);
    setEmployeeForm({
      name: emp.name,
      position: emp.position,
      salary: emp.salary.toString(),
      hire_date: emp.hire_date || '',
      status: emp.status,
      notes: emp.notes || ''
    });
  };

  const handleDeleteEmployee = async (id) => {
    try {
      await operationalCostsAPI.deleteEmployee(id);
      toast.success('Empleado eliminado');
      fetchData();
    } catch (error) {
      toast.error('Error al eliminar');
    }
  };

  // === PAYROLL HANDLERS ===
  const resetPayrollForm = () => {
    setPayrollForm({ employee_id: '', period: selectedPeriod, amount: '', payment_date: '', notes: '' });
  };

  const handleSavePayroll = async (e) => {
    e.preventDefault();
    try {
      await operationalCostsAPI.createPayroll({
        ...payrollForm,
        amount: parseFloat(payrollForm.amount)
      });
      toast.success('Pago de nómina registrado');
      resetPayrollForm();
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al guardar');
    }
  };

  const handleDeletePayroll = async (id) => {
    try {
      await operationalCostsAPI.deletePayroll(id);
      toast.success('Pago eliminado');
      fetchData();
    } catch (error) {
      toast.error('Error al eliminar');
    }
  };

  // === ELECTRICITY HANDLERS ===
  const resetElectricityForm = () => {
    setElectricityForm({ period: selectedPeriod, amount: '', kwh: '', invoice_number: '', payment_date: '', notes: '' });
    setEditingElectricity(null);
  };

  const handleSaveElectricity = async (e) => {
    e.preventDefault();
    try {
      const data = {
        ...electricityForm,
        amount: parseFloat(electricityForm.amount),
        kwh: electricityForm.kwh ? parseFloat(electricityForm.kwh) : null
      };
      if (editingElectricity) {
        await operationalCostsAPI.updateElectricityExpense(editingElectricity.id, data);
        toast.success('Gasto actualizado');
      } else {
        await operationalCostsAPI.createElectricityExpense(data);
        toast.success('Gasto de luz registrado');
      }
      resetElectricityForm();
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al guardar');
    }
  };

  const handleEditElectricity = (exp) => {
    setEditingElectricity(exp);
    setElectricityForm({
      period: exp.period,
      amount: exp.amount.toString(),
      kwh: exp.kwh?.toString() || '',
      invoice_number: exp.invoice_number || '',
      payment_date: exp.payment_date || '',
      notes: exp.notes || ''
    });
  };

  const handleDeleteElectricity = async (id) => {
    try {
      await operationalCostsAPI.deleteElectricityExpense(id);
      toast.success('Gasto eliminado');
      fetchData();
    } catch (error) {
      toast.error('Error al eliminar');
    }
  };

  // === MAINTENANCE HANDLERS ===
  const resetMaintenanceForm = () => {
    setMaintenanceForm({ description: '', equipment_type: '', amount: '', expense_date: '', technician: '', notes: '' });
    setEditingMaintenance(null);
  };

  const handleSaveMaintenance = async (e) => {
    e.preventDefault();
    try {
      const data = {
        ...maintenanceForm,
        amount: parseFloat(maintenanceForm.amount)
      };
      if (editingMaintenance) {
        await operationalCostsAPI.updateMaintenanceExpense(editingMaintenance.id, data);
        toast.success('Gasto actualizado');
      } else {
        await operationalCostsAPI.createMaintenanceExpense(data);
        toast.success('Gasto de mantenimiento registrado');
      }
      resetMaintenanceForm();
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al guardar');
    }
  };

  const handleEditMaintenance = (exp) => {
    setEditingMaintenance(exp);
    setMaintenanceForm({
      description: exp.description,
      equipment_type: exp.equipment_type,
      amount: exp.amount.toString(),
      expense_date: exp.expense_date,
      technician: exp.technician || '',
      notes: exp.notes || ''
    });
  };

  const handleDeleteMaintenance = async (id) => {
    try {
      await operationalCostsAPI.deleteMaintenanceExpense(id);
      toast.success('Gasto eliminado');
      fetchData();
    } catch (error) {
      toast.error('Error al eliminar');
    }
  };

  // === GASOIL HANDLERS ===
  const resetGasoilForm = () => {
    setGasoilForm({ liters: '', price_per_liter: '', expense_date: '', supplier: '', invoice_number: '', notes: '' });
    setEditingGasoil(null);
  };

  const handleSaveGasoil = async (e) => {
    e.preventDefault();
    try {
      const data = {
        liters: parseFloat(gasoilForm.liters),
        price_per_liter: parseFloat(gasoilForm.price_per_liter),
        expense_date: gasoilForm.expense_date,
        supplier: gasoilForm.supplier,
        invoice_number: gasoilForm.invoice_number,
        notes: gasoilForm.notes
      };
      if (editingGasoil) {
        await operationalCostsAPI.updateGasoilExpense(editingGasoil.id, data);
        toast.success('Gasto actualizado');
      } else {
        await operationalCostsAPI.createGasoilExpense(data);
        toast.success('Gasto de gasoil registrado');
      }
      resetGasoilForm();
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al guardar');
    }
  };

  const handleEditGasoil = (exp) => {
    setEditingGasoil(exp);
    setGasoilForm({
      liters: exp.liters.toString(),
      price_per_liter: exp.price_per_liter.toString(),
      expense_date: exp.expense_date,
      supplier: exp.supplier || '',
      invoice_number: exp.invoice_number || '',
      notes: exp.notes || ''
    });
  };

  const handleDeleteGasoil = async (id) => {
    try {
      await operationalCostsAPI.deleteGasoilExpense(id);
      toast.success('Gasto eliminado');
      fetchData();
    } catch (error) {
      toast.error('Error al eliminar');
    }
  };

  // Select employee and auto-fill salary
  const handleEmployeeSelect = (empId) => {
    const emp = employees.find(e => e.id === empId);
    if (emp) {
      setPayrollForm({
        ...payrollForm,
        employee_id: empId,
        amount: emp.salary.toString()
      });
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin h-8 w-8 border-4 border-[#931A25] border-t-transparent rounded-full"></div>
      </div>
    );
  }

  // Calculate totals
  const totalPayroll = payroll.reduce((sum, p) => sum + p.amount, 0);
  const totalElectricity = electricityExpenses.reduce((sum, e) => sum + e.amount, 0);
  const totalMaintenance = maintenanceExpenses.reduce((sum, m) => sum + m.amount, 0);
  const totalGasoil = gasoilExpenses.reduce((sum, g) => sum + g.amount, 0);
  const totalCosts = totalPayroll + totalElectricity + totalMaintenance + totalGasoil;

  return (
    <div className="space-y-6" data-testid="operational-costs-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold text-slate-900">Costos Operativos</h1>
          <p className="text-slate-500 mt-1">Gestión de nómina, electricidad, mantenimiento y gasoil</p>
        </div>
        <div className="flex items-center gap-3">
          <Select value={selectedPeriod} onValueChange={setSelectedPeriod}>
            <SelectTrigger className="w-48" data-testid="period-selector">
              <Calendar className="h-4 w-4 mr-2" />
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {periodOptions.map(opt => (
                <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4">
        <Card className="border-slate-200" data-testid="card-total-costs">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-[#931A25]/10 flex items-center justify-center">
                <Calculator className="h-5 w-5 text-[#931A25]" />
              </div>
              <div>
                <p className="text-xs text-slate-500">Total Costos</p>
                <p className="font-bold text-lg text-[#931A25]">{formatCurrency(totalCosts)}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="border-slate-200" data-testid="card-payroll">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center">
                <Users className="h-5 w-5 text-blue-600" />
              </div>
              <div>
                <p className="text-xs text-slate-500">Nómina</p>
                <p className="font-bold text-lg">{formatCurrency(totalPayroll)}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="border-slate-200" data-testid="card-electricity">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-yellow-100 flex items-center justify-center">
                <Zap className="h-5 w-5 text-yellow-600" />
              </div>
              <div>
                <p className="text-xs text-slate-500">Electricidad</p>
                <p className="font-bold text-lg">{formatCurrency(totalElectricity)}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="border-slate-200" data-testid="card-maintenance">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-orange-100 flex items-center justify-center">
                <Wrench className="h-5 w-5 text-orange-600" />
              </div>
              <div>
                <p className="text-xs text-slate-500">Mantenimiento</p>
                <p className="font-bold text-lg">{formatCurrency(totalMaintenance)}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="border-slate-200" data-testid="card-gasoil">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-red-100 flex items-center justify-center">
                <Fuel className="h-5 w-5 text-red-600" />
              </div>
              <div>
                <p className="text-xs text-slate-500">Gasoil</p>
                <p className="font-bold text-lg">{formatCurrency(totalGasoil)}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="border-slate-200 bg-gradient-to-r from-[#931A25]/5 to-[#E97171]/5" data-testid="card-cost-per-qq">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-green-100 flex items-center justify-center">
                <TrendingUp className="h-5 w-5 text-green-600" />
              </div>
              <div>
                <p className="text-xs text-slate-500">Costo/Quintal</p>
                <p className="font-bold text-lg text-green-600">
                  {costSummary ? formatCurrency(costSummary.cost_per_quintal) : 'N/A'}
                </p>
                {costSummary && (
                  <p className="text-xs text-slate-400">{costSummary.production_quantity.toFixed(2)} qq producidos</p>
                )}
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-4">
        <TabsList className="grid w-full grid-cols-6 lg:w-auto lg:inline-grid">
          <TabsTrigger value="summary" className="text-xs sm:text-sm">Resumen</TabsTrigger>
          <TabsTrigger value="employees" className="text-xs sm:text-sm">Empleados</TabsTrigger>
          <TabsTrigger value="payroll" className="text-xs sm:text-sm">Nómina</TabsTrigger>
          <TabsTrigger value="electricity" className="text-xs sm:text-sm">Electricidad</TabsTrigger>
          <TabsTrigger value="maintenance" className="text-xs sm:text-sm">Mantenimiento</TabsTrigger>
          <TabsTrigger value="gasoil" className="text-xs sm:text-sm">Gasoil</TabsTrigger>
        </TabsList>

        {/* SUMMARY TAB */}
        <TabsContent value="summary">
          <Card className="border-slate-200">
            <CardHeader>
              <CardTitle>Resumen de Costos - {periodOptions.find(p => p.value === selectedPeriod)?.label}</CardTitle>
            </CardHeader>
            <CardContent>
              {costSummary ? (
                <div className="space-y-6">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div className="space-y-4">
                      <h3 className="font-semibold text-slate-700">Desglose de Costos</h3>
                      <div className="space-y-2">
                        <div className="flex justify-between p-3 bg-blue-50 rounded-lg">
                          <span className="flex items-center gap-2"><Users className="h-4 w-4 text-blue-600" /> Nómina</span>
                          <span className="font-semibold">{formatCurrency(costSummary.total_payroll)}</span>
                        </div>
                        <div className="flex justify-between p-3 bg-yellow-50 rounded-lg">
                          <span className="flex items-center gap-2"><Zap className="h-4 w-4 text-yellow-600" /> Electricidad</span>
                          <span className="font-semibold">{formatCurrency(costSummary.total_electricity)}</span>
                        </div>
                        <div className="flex justify-between p-3 bg-orange-50 rounded-lg">
                          <span className="flex items-center gap-2"><Wrench className="h-4 w-4 text-orange-600" /> Mantenimiento</span>
                          <span className="font-semibold">{formatCurrency(costSummary.total_maintenance)}</span>
                        </div>
                        <div className="flex justify-between p-3 bg-red-50 rounded-lg">
                          <span className="flex items-center gap-2"><Fuel className="h-4 w-4 text-red-600" /> Gasoil</span>
                          <span className="font-semibold">{formatCurrency(costSummary.total_gasoil || 0)}</span>
                        </div>
                        <div className="flex justify-between p-3 bg-[#931A25]/10 rounded-lg font-bold">
                          <span>Total Costos Operativos</span>
                          <span className="text-[#931A25]">{formatCurrency(costSummary.total_costs)}</span>
                        </div>
                      </div>
                    </div>
                    <div className="space-y-4">
                      <h3 className="font-semibold text-slate-700">Producción y Costo Unitario</h3>
                      <div className="p-6 bg-gradient-to-br from-green-50 to-emerald-50 rounded-lg text-center">
                        <p className="text-sm text-slate-500 mb-2">Producción del Período</p>
                        <p className="text-3xl font-bold text-slate-800">{costSummary.production_quantity.toFixed(2)} qq</p>
                        <div className="mt-4 pt-4 border-t border-green-200">
                          <p className="text-sm text-slate-500 mb-1">Costo Operativo por Quintal</p>
                          <p className="text-2xl font-bold text-green-600">{formatCurrency(costSummary.cost_per_quintal)}/qq</p>
                        </div>
                      </div>
                      <p className="text-xs text-slate-500 text-center">
                        Este costo se suma al costo de materias primas para obtener el costo total de producción.
                      </p>
                    </div>
                  </div>
                </div>
              ) : (
                <div className="text-center py-8 text-slate-500">
                  <Calculator className="h-12 w-12 mx-auto mb-4 text-slate-300" />
                  <p>No hay datos de costos para este período.</p>
                  <p className="text-sm">Registra gastos de nómina, electricidad o mantenimiento.</p>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* EMPLOYEES TAB */}
        <TabsContent value="employees">
          <Card className="border-slate-200">
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle>Empleados</CardTitle>
              <Dialog onOpenChange={(open) => !open && resetEmployeeForm()}>
                <DialogTrigger asChild>
                  <Button className="bg-[#931A25] hover:bg-[#7A151F]" data-testid="add-employee-btn">
                    <Plus className="h-4 w-4 mr-2" />
                    Nuevo Empleado
                  </Button>
                </DialogTrigger>
                <DialogContent>
                  <DialogHeader>
                    <DialogTitle>{editingEmployee ? 'Editar Empleado' : 'Nuevo Empleado'}</DialogTitle>
                    <DialogDescription>Registra los datos del empleado para el cálculo de nómina.</DialogDescription>
                  </DialogHeader>
                  <form onSubmit={handleSaveEmployee} className="space-y-4">
                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2 col-span-2">
                        <Label>Nombre Completo *</Label>
                        <Input
                          value={employeeForm.name}
                          onChange={(e) => setEmployeeForm({...employeeForm, name: e.target.value})}
                          required
                          data-testid="employee-name-input"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label>Cargo *</Label>
                        <Select value={employeeForm.position} onValueChange={(v) => setEmployeeForm({...employeeForm, position: v})}>
                          <SelectTrigger data-testid="employee-position-select">
                            <SelectValue placeholder="Seleccionar" />
                          </SelectTrigger>
                          <SelectContent>
                            {POSITIONS.map(pos => (
                              <SelectItem key={pos} value={pos}>{pos}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="space-y-2">
                        <Label>Salario Mensual (RD$) *</Label>
                        <Input
                          type="number"
                          step="0.01"
                          min="0"
                          value={employeeForm.salary}
                          onChange={(e) => setEmployeeForm({...employeeForm, salary: e.target.value})}
                          required
                          data-testid="employee-salary-input"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label>Fecha de Ingreso</Label>
                        <Input
                          type="date"
                          value={employeeForm.hire_date}
                          onChange={(e) => setEmployeeForm({...employeeForm, hire_date: e.target.value})}
                        />
                      </div>
                      <div className="space-y-2">
                        <Label>Estado</Label>
                        <Select value={employeeForm.status} onValueChange={(v) => setEmployeeForm({...employeeForm, status: v})}>
                          <SelectTrigger>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="activo">Activo</SelectItem>
                            <SelectItem value="inactivo">Inactivo</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                    <div className="space-y-2">
                      <Label>Notas</Label>
                      <Textarea
                        value={employeeForm.notes}
                        onChange={(e) => setEmployeeForm({...employeeForm, notes: e.target.value})}
                        placeholder="Notas adicionales..."
                      />
                    </div>
                    <DialogFooter>
                      <DialogClose asChild>
                        <Button type="button" variant="outline" onClick={resetEmployeeForm}>Cancelar</Button>
                      </DialogClose>
                      <Button type="submit" className="bg-[#931A25] hover:bg-[#7A151F]" data-testid="save-employee-btn">
                        {editingEmployee ? 'Actualizar' : 'Guardar'}
                      </Button>
                    </DialogFooter>
                  </form>
                </DialogContent>
              </Dialog>
            </CardHeader>
            <CardContent className="p-0">
              <Table>
                <TableHeader>
                  <TableRow className="bg-slate-50">
                    <TableHead>Nombre</TableHead>
                    <TableHead>Cargo</TableHead>
                    <TableHead className="text-right">Salario</TableHead>
                    <TableHead>Ingreso</TableHead>
                    <TableHead className="text-center">Estado</TableHead>
                    <TableHead className="text-center">Acciones</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {employees.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={6} className="text-center py-8 text-slate-500">
                        No hay empleados registrados
                      </TableCell>
                    </TableRow>
                  ) : (
                    employees.map((emp) => (
                      <TableRow key={emp.id}>
                        <TableCell className="font-medium">{emp.name}</TableCell>
                        <TableCell>{emp.position}</TableCell>
                        <TableCell className="text-right">{formatCurrency(emp.salary)}</TableCell>
                        <TableCell>{emp.hire_date ? formatDate(emp.hire_date) : '-'}</TableCell>
                        <TableCell className="text-center">
                          <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                            emp.status === 'activo' ? 'bg-green-100 text-green-700' : 'bg-slate-100 text-slate-600'
                          }`}>
                            {emp.status === 'activo' ? 'Activo' : 'Inactivo'}
                          </span>
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center justify-center gap-1">
                            <Dialog onOpenChange={(open) => open && handleEditEmployee(emp)}>
                              <DialogTrigger asChild>
                                <Button variant="ghost" size="sm" className="text-blue-600">
                                  <Edit className="h-4 w-4" />
                                </Button>
                              </DialogTrigger>
                              <DialogContent>
                                <DialogHeader>
                                  <DialogTitle>Editar Empleado</DialogTitle>
                                  <DialogDescription>Actualiza los datos del empleado.</DialogDescription>
                                </DialogHeader>
                                <form onSubmit={handleSaveEmployee} className="space-y-4">
                                  <div className="grid grid-cols-2 gap-4">
                                    <div className="space-y-2 col-span-2">
                                      <Label>Nombre Completo *</Label>
                                      <Input
                                        value={employeeForm.name}
                                        onChange={(e) => setEmployeeForm({...employeeForm, name: e.target.value})}
                                        required
                                      />
                                    </div>
                                    <div className="space-y-2">
                                      <Label>Cargo *</Label>
                                      <Select value={employeeForm.position} onValueChange={(v) => setEmployeeForm({...employeeForm, position: v})}>
                                        <SelectTrigger>
                                          <SelectValue placeholder="Seleccionar" />
                                        </SelectTrigger>
                                        <SelectContent>
                                          {POSITIONS.map(pos => (
                                            <SelectItem key={pos} value={pos}>{pos}</SelectItem>
                                          ))}
                                        </SelectContent>
                                      </Select>
                                    </div>
                                    <div className="space-y-2">
                                      <Label>Salario Mensual (RD$) *</Label>
                                      <Input
                                        type="number"
                                        step="0.01"
                                        min="0"
                                        value={employeeForm.salary}
                                        onChange={(e) => setEmployeeForm({...employeeForm, salary: e.target.value})}
                                        required
                                      />
                                    </div>
                                    <div className="space-y-2">
                                      <Label>Fecha de Ingreso</Label>
                                      <Input
                                        type="date"
                                        value={employeeForm.hire_date}
                                        onChange={(e) => setEmployeeForm({...employeeForm, hire_date: e.target.value})}
                                      />
                                    </div>
                                    <div className="space-y-2">
                                      <Label>Estado</Label>
                                      <Select value={employeeForm.status} onValueChange={(v) => setEmployeeForm({...employeeForm, status: v})}>
                                        <SelectTrigger>
                                          <SelectValue />
                                        </SelectTrigger>
                                        <SelectContent>
                                          <SelectItem value="activo">Activo</SelectItem>
                                          <SelectItem value="inactivo">Inactivo</SelectItem>
                                        </SelectContent>
                                      </Select>
                                    </div>
                                  </div>
                                  <div className="space-y-2">
                                    <Label>Notas</Label>
                                    <Textarea
                                      value={employeeForm.notes}
                                      onChange={(e) => setEmployeeForm({...employeeForm, notes: e.target.value})}
                                    />
                                  </div>
                                  <DialogFooter>
                                    <DialogClose asChild>
                                      <Button type="button" variant="outline" onClick={resetEmployeeForm}>Cancelar</Button>
                                    </DialogClose>
                                    <Button type="submit" className="bg-[#931A25] hover:bg-[#7A151F]">Actualizar</Button>
                                  </DialogFooter>
                                </form>
                              </DialogContent>
                            </Dialog>
                            <AlertDialog>
                              <AlertDialogTrigger asChild>
                                <Button variant="ghost" size="sm" className="text-red-600">
                                  <Trash2 className="h-4 w-4" />
                                </Button>
                              </AlertDialogTrigger>
                              <AlertDialogContent>
                                <AlertDialogHeader>
                                  <AlertDialogTitle>Eliminar empleado</AlertDialogTitle>
                                  <AlertDialogDescription>
                                    ¿Estás seguro de eliminar a {emp.name}? Esta acción no se puede deshacer.
                                  </AlertDialogDescription>
                                </AlertDialogHeader>
                                <AlertDialogFooter>
                                  <AlertDialogCancel>Cancelar</AlertDialogCancel>
                                  <AlertDialogAction onClick={() => handleDeleteEmployee(emp.id)} className="bg-red-600 hover:bg-red-700">
                                    Eliminar
                                  </AlertDialogAction>
                                </AlertDialogFooter>
                              </AlertDialogContent>
                            </AlertDialog>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>

        {/* PAYROLL TAB */}
        <TabsContent value="payroll">
          <Card className="border-slate-200">
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle>Pagos de Nómina - {periodOptions.find(p => p.value === selectedPeriod)?.label}</CardTitle>
              <Dialog onOpenChange={(open) => !open && resetPayrollForm()}>
                <DialogTrigger asChild>
                  <Button className="bg-[#931A25] hover:bg-[#7A151F]" data-testid="add-payroll-btn">
                    <Plus className="h-4 w-4 mr-2" />
                    Registrar Pago
                  </Button>
                </DialogTrigger>
                <DialogContent>
                  <DialogHeader>
                    <DialogTitle>Registrar Pago de Nómina</DialogTitle>
                    <DialogDescription>Registra el pago de salario a un empleado.</DialogDescription>
                  </DialogHeader>
                  <form onSubmit={handleSavePayroll} className="space-y-4">
                    <div className="space-y-2">
                      <Label>Empleado *</Label>
                      <Select value={payrollForm.employee_id} onValueChange={handleEmployeeSelect}>
                        <SelectTrigger data-testid="payroll-employee-select">
                          <SelectValue placeholder="Seleccionar empleado" />
                        </SelectTrigger>
                        <SelectContent>
                          {employees.filter(e => e.status === 'activo').map(emp => (
                            <SelectItem key={emp.id} value={emp.id}>
                              {emp.name} - {emp.position}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label>Período *</Label>
                        <Select value={payrollForm.period} onValueChange={(v) => setPayrollForm({...payrollForm, period: v})}>
                          <SelectTrigger>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            {periodOptions.map(opt => (
                              <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="space-y-2">
                        <Label>Monto (RD$) *</Label>
                        <Input
                          type="number"
                          step="0.01"
                          min="0"
                          value={payrollForm.amount}
                          onChange={(e) => setPayrollForm({...payrollForm, amount: e.target.value})}
                          required
                          data-testid="payroll-amount-input"
                        />
                      </div>
                    </div>
                    <div className="space-y-2">
                      <Label>Fecha de Pago *</Label>
                      <Input
                        type="date"
                        value={payrollForm.payment_date}
                        onChange={(e) => setPayrollForm({...payrollForm, payment_date: e.target.value})}
                        required
                        data-testid="payroll-date-input"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Notas</Label>
                      <Textarea
                        value={payrollForm.notes}
                        onChange={(e) => setPayrollForm({...payrollForm, notes: e.target.value})}
                        placeholder="Bonificación, descuentos, etc..."
                      />
                    </div>
                    <DialogFooter>
                      <DialogClose asChild>
                        <Button type="button" variant="outline" onClick={resetPayrollForm}>Cancelar</Button>
                      </DialogClose>
                      <Button type="submit" className="bg-[#931A25] hover:bg-[#7A151F]" data-testid="save-payroll-btn">
                        Registrar
                      </Button>
                    </DialogFooter>
                  </form>
                </DialogContent>
              </Dialog>
            </CardHeader>
            <CardContent className="p-0">
              <Table>
                <TableHeader>
                  <TableRow className="bg-slate-50">
                    <TableHead>Empleado</TableHead>
                    <TableHead>Período</TableHead>
                    <TableHead className="text-right">Monto</TableHead>
                    <TableHead>Fecha Pago</TableHead>
                    <TableHead>Notas</TableHead>
                    <TableHead className="text-center">Acciones</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {payroll.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={6} className="text-center py-8 text-slate-500">
                        No hay pagos de nómina en este período
                      </TableCell>
                    </TableRow>
                  ) : (
                    payroll.map((pay) => (
                      <TableRow key={pay.id}>
                        <TableCell className="font-medium">{pay.employee_name}</TableCell>
                        <TableCell>{pay.period}</TableCell>
                        <TableCell className="text-right font-semibold">{formatCurrency(pay.amount)}</TableCell>
                        <TableCell>{formatDate(pay.payment_date)}</TableCell>
                        <TableCell className="max-w-[200px] truncate">{pay.notes || '-'}</TableCell>
                        <TableCell className="text-center">
                          <AlertDialog>
                            <AlertDialogTrigger asChild>
                              <Button variant="ghost" size="sm" className="text-red-600">
                                <Trash2 className="h-4 w-4" />
                              </Button>
                            </AlertDialogTrigger>
                            <AlertDialogContent>
                              <AlertDialogHeader>
                                <AlertDialogTitle>Eliminar pago</AlertDialogTitle>
                                <AlertDialogDescription>
                                  ¿Estás seguro de eliminar este pago de nómina?
                                </AlertDialogDescription>
                              </AlertDialogHeader>
                              <AlertDialogFooter>
                                <AlertDialogCancel>Cancelar</AlertDialogCancel>
                                <AlertDialogAction onClick={() => handleDeletePayroll(pay.id)} className="bg-red-600 hover:bg-red-700">
                                  Eliminar
                                </AlertDialogAction>
                              </AlertDialogFooter>
                            </AlertDialogContent>
                          </AlertDialog>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
              {payroll.length > 0 && (
                <div className="p-4 bg-slate-50 border-t flex justify-end">
                  <span className="font-semibold">Total Nómina: {formatCurrency(totalPayroll)}</span>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* ELECTRICITY TAB */}
        <TabsContent value="electricity">
          <Card className="border-slate-200">
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle>Gastos de Electricidad</CardTitle>
              <Dialog onOpenChange={(open) => !open && resetElectricityForm()}>
                <DialogTrigger asChild>
                  <Button className="bg-[#931A25] hover:bg-[#7A151F]" data-testid="add-electricity-btn">
                    <Plus className="h-4 w-4 mr-2" />
                    Registrar Gasto
                  </Button>
                </DialogTrigger>
                <DialogContent>
                  <DialogHeader>
                    <DialogTitle>{editingElectricity ? 'Editar Gasto' : 'Nuevo Gasto de Electricidad'}</DialogTitle>
                    <DialogDescription>Registra la factura de luz del período.</DialogDescription>
                  </DialogHeader>
                  <form onSubmit={handleSaveElectricity} className="space-y-4">
                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label>Período *</Label>
                        <Select value={electricityForm.period} onValueChange={(v) => setElectricityForm({...electricityForm, period: v})}>
                          <SelectTrigger>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            {periodOptions.map(opt => (
                              <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="space-y-2">
                        <Label>Monto (RD$) *</Label>
                        <Input
                          type="number"
                          step="0.01"
                          min="0"
                          value={electricityForm.amount}
                          onChange={(e) => setElectricityForm({...electricityForm, amount: e.target.value})}
                          required
                          data-testid="electricity-amount-input"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label>Consumo (kWh)</Label>
                        <Input
                          type="number"
                          step="0.01"
                          min="0"
                          value={electricityForm.kwh}
                          onChange={(e) => setElectricityForm({...electricityForm, kwh: e.target.value})}
                        />
                      </div>
                      <div className="space-y-2">
                        <Label>No. Factura</Label>
                        <Input
                          value={electricityForm.invoice_number}
                          onChange={(e) => setElectricityForm({...electricityForm, invoice_number: e.target.value})}
                        />
                      </div>
                    </div>
                    <div className="space-y-2">
                      <Label>Fecha de Pago</Label>
                      <Input
                        type="date"
                        value={electricityForm.payment_date}
                        onChange={(e) => setElectricityForm({...electricityForm, payment_date: e.target.value})}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Notas</Label>
                      <Textarea
                        value={electricityForm.notes}
                        onChange={(e) => setElectricityForm({...electricityForm, notes: e.target.value})}
                        placeholder="Observaciones..."
                      />
                    </div>
                    <DialogFooter>
                      <DialogClose asChild>
                        <Button type="button" variant="outline" onClick={resetElectricityForm}>Cancelar</Button>
                      </DialogClose>
                      <Button type="submit" className="bg-[#931A25] hover:bg-[#7A151F]" data-testid="save-electricity-btn">
                        {editingElectricity ? 'Actualizar' : 'Guardar'}
                      </Button>
                    </DialogFooter>
                  </form>
                </DialogContent>
              </Dialog>
            </CardHeader>
            <CardContent className="p-0">
              <Table>
                <TableHeader>
                  <TableRow className="bg-slate-50">
                    <TableHead>Período</TableHead>
                    <TableHead className="text-right">Monto</TableHead>
                    <TableHead className="text-right">kWh</TableHead>
                    <TableHead>No. Factura</TableHead>
                    <TableHead>Fecha Pago</TableHead>
                    <TableHead className="text-center">Acciones</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {electricityExpenses.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={6} className="text-center py-8 text-slate-500">
                        No hay gastos de electricidad registrados
                      </TableCell>
                    </TableRow>
                  ) : (
                    electricityExpenses.map((exp) => (
                      <TableRow key={exp.id}>
                        <TableCell className="font-medium">{exp.period}</TableCell>
                        <TableCell className="text-right font-semibold">{formatCurrency(exp.amount)}</TableCell>
                        <TableCell className="text-right">{exp.kwh?.toLocaleString() || '-'}</TableCell>
                        <TableCell>{exp.invoice_number || '-'}</TableCell>
                        <TableCell>{exp.payment_date ? formatDate(exp.payment_date) : '-'}</TableCell>
                        <TableCell>
                          <div className="flex items-center justify-center gap-1">
                            <Dialog onOpenChange={(open) => open && handleEditElectricity(exp)}>
                              <DialogTrigger asChild>
                                <Button variant="ghost" size="sm" className="text-blue-600">
                                  <Edit className="h-4 w-4" />
                                </Button>
                              </DialogTrigger>
                              <DialogContent>
                                <DialogHeader>
                                  <DialogTitle>Editar Gasto de Electricidad</DialogTitle>
                                  <DialogDescription>Actualiza los datos del gasto.</DialogDescription>
                                </DialogHeader>
                                <form onSubmit={handleSaveElectricity} className="space-y-4">
                                  <div className="grid grid-cols-2 gap-4">
                                    <div className="space-y-2">
                                      <Label>Período *</Label>
                                      <Select value={electricityForm.period} onValueChange={(v) => setElectricityForm({...electricityForm, period: v})}>
                                        <SelectTrigger>
                                          <SelectValue />
                                        </SelectTrigger>
                                        <SelectContent>
                                          {periodOptions.map(opt => (
                                            <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                                          ))}
                                        </SelectContent>
                                      </Select>
                                    </div>
                                    <div className="space-y-2">
                                      <Label>Monto (RD$) *</Label>
                                      <Input
                                        type="number"
                                        step="0.01"
                                        min="0"
                                        value={electricityForm.amount}
                                        onChange={(e) => setElectricityForm({...electricityForm, amount: e.target.value})}
                                        required
                                      />
                                    </div>
                                    <div className="space-y-2">
                                      <Label>Consumo (kWh)</Label>
                                      <Input
                                        type="number"
                                        step="0.01"
                                        min="0"
                                        value={electricityForm.kwh}
                                        onChange={(e) => setElectricityForm({...electricityForm, kwh: e.target.value})}
                                      />
                                    </div>
                                    <div className="space-y-2">
                                      <Label>No. Factura</Label>
                                      <Input
                                        value={electricityForm.invoice_number}
                                        onChange={(e) => setElectricityForm({...electricityForm, invoice_number: e.target.value})}
                                      />
                                    </div>
                                  </div>
                                  <div className="space-y-2">
                                    <Label>Fecha de Pago</Label>
                                    <Input
                                      type="date"
                                      value={electricityForm.payment_date}
                                      onChange={(e) => setElectricityForm({...electricityForm, payment_date: e.target.value})}
                                    />
                                  </div>
                                  <div className="space-y-2">
                                    <Label>Notas</Label>
                                    <Textarea
                                      value={electricityForm.notes}
                                      onChange={(e) => setElectricityForm({...electricityForm, notes: e.target.value})}
                                    />
                                  </div>
                                  <DialogFooter>
                                    <DialogClose asChild>
                                      <Button type="button" variant="outline" onClick={resetElectricityForm}>Cancelar</Button>
                                    </DialogClose>
                                    <Button type="submit" className="bg-[#931A25] hover:bg-[#7A151F]">Actualizar</Button>
                                  </DialogFooter>
                                </form>
                              </DialogContent>
                            </Dialog>
                            <AlertDialog>
                              <AlertDialogTrigger asChild>
                                <Button variant="ghost" size="sm" className="text-red-600">
                                  <Trash2 className="h-4 w-4" />
                                </Button>
                              </AlertDialogTrigger>
                              <AlertDialogContent>
                                <AlertDialogHeader>
                                  <AlertDialogTitle>Eliminar gasto</AlertDialogTitle>
                                  <AlertDialogDescription>
                                    ¿Estás seguro de eliminar este gasto de electricidad?
                                  </AlertDialogDescription>
                                </AlertDialogHeader>
                                <AlertDialogFooter>
                                  <AlertDialogCancel>Cancelar</AlertDialogCancel>
                                  <AlertDialogAction onClick={() => handleDeleteElectricity(exp.id)} className="bg-red-600 hover:bg-red-700">
                                    Eliminar
                                  </AlertDialogAction>
                                </AlertDialogFooter>
                              </AlertDialogContent>
                            </AlertDialog>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
              {electricityExpenses.length > 0 && (
                <div className="p-4 bg-slate-50 border-t flex justify-end">
                  <span className="font-semibold">Total Electricidad: {formatCurrency(totalElectricity)}</span>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* MAINTENANCE TAB */}
        <TabsContent value="maintenance">
          <Card className="border-slate-200">
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle>Gastos de Mantenimiento</CardTitle>
              <Dialog onOpenChange={(open) => !open && resetMaintenanceForm()}>
                <DialogTrigger asChild>
                  <Button className="bg-[#931A25] hover:bg-[#7A151F]" data-testid="add-maintenance-btn">
                    <Plus className="h-4 w-4 mr-2" />
                    Registrar Gasto
                  </Button>
                </DialogTrigger>
                <DialogContent>
                  <DialogHeader>
                    <DialogTitle>{editingMaintenance ? 'Editar Gasto' : 'Nuevo Gasto de Mantenimiento'}</DialogTitle>
                    <DialogDescription>Registra reparaciones de motores, equipos, etc.</DialogDescription>
                  </DialogHeader>
                  <form onSubmit={handleSaveMaintenance} className="space-y-4">
                    <div className="space-y-2">
                      <Label>Descripción *</Label>
                      <Input
                        value={maintenanceForm.description}
                        onChange={(e) => setMaintenanceForm({...maintenanceForm, description: e.target.value})}
                        placeholder="Ej: Reparación motor eléctrico mezcladora"
                        required
                        data-testid="maintenance-description-input"
                      />
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label>Tipo de Equipo *</Label>
                        <Select value={maintenanceForm.equipment_type} onValueChange={(v) => setMaintenanceForm({...maintenanceForm, equipment_type: v})}>
                          <SelectTrigger data-testid="maintenance-equipment-select">
                            <SelectValue placeholder="Seleccionar" />
                          </SelectTrigger>
                          <SelectContent>
                            {EQUIPMENT_TYPES.map(type => (
                              <SelectItem key={type} value={type}>{type}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="space-y-2">
                        <Label>Monto (RD$) *</Label>
                        <Input
                          type="number"
                          step="0.01"
                          min="0"
                          value={maintenanceForm.amount}
                          onChange={(e) => setMaintenanceForm({...maintenanceForm, amount: e.target.value})}
                          required
                          data-testid="maintenance-amount-input"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label>Fecha *</Label>
                        <Input
                          type="date"
                          value={maintenanceForm.expense_date}
                          onChange={(e) => setMaintenanceForm({...maintenanceForm, expense_date: e.target.value})}
                          required
                          data-testid="maintenance-date-input"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label>Técnico</Label>
                        <Input
                          value={maintenanceForm.technician}
                          onChange={(e) => setMaintenanceForm({...maintenanceForm, technician: e.target.value})}
                          placeholder="Nombre del técnico"
                        />
                      </div>
                    </div>
                    <div className="space-y-2">
                      <Label>Notas</Label>
                      <Textarea
                        value={maintenanceForm.notes}
                        onChange={(e) => setMaintenanceForm({...maintenanceForm, notes: e.target.value})}
                        placeholder="Detalles adicionales..."
                      />
                    </div>
                    <DialogFooter>
                      <DialogClose asChild>
                        <Button type="button" variant="outline" onClick={resetMaintenanceForm}>Cancelar</Button>
                      </DialogClose>
                      <Button type="submit" className="bg-[#931A25] hover:bg-[#7A151F]" data-testid="save-maintenance-btn">
                        {editingMaintenance ? 'Actualizar' : 'Guardar'}
                      </Button>
                    </DialogFooter>
                  </form>
                </DialogContent>
              </Dialog>
            </CardHeader>
            <CardContent className="p-0">
              <Table>
                <TableHeader>
                  <TableRow className="bg-slate-50">
                    <TableHead>Fecha</TableHead>
                    <TableHead>Descripción</TableHead>
                    <TableHead>Equipo</TableHead>
                    <TableHead className="text-right">Monto</TableHead>
                    <TableHead>Técnico</TableHead>
                    <TableHead className="text-center">Acciones</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {maintenanceExpenses.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={6} className="text-center py-8 text-slate-500">
                        No hay gastos de mantenimiento registrados
                      </TableCell>
                    </TableRow>
                  ) : (
                    maintenanceExpenses.map((exp) => (
                      <TableRow key={exp.id}>
                        <TableCell>{formatDate(exp.expense_date)}</TableCell>
                        <TableCell className="font-medium max-w-[200px] truncate">{exp.description}</TableCell>
                        <TableCell>
                          <span className="px-2 py-1 bg-orange-100 text-orange-700 rounded text-xs">
                            {exp.equipment_type}
                          </span>
                        </TableCell>
                        <TableCell className="text-right font-semibold">{formatCurrency(exp.amount)}</TableCell>
                        <TableCell>{exp.technician || '-'}</TableCell>
                        <TableCell>
                          <div className="flex items-center justify-center gap-1">
                            <Dialog onOpenChange={(open) => open && handleEditMaintenance(exp)}>
                              <DialogTrigger asChild>
                                <Button variant="ghost" size="sm" className="text-blue-600">
                                  <Edit className="h-4 w-4" />
                                </Button>
                              </DialogTrigger>
                              <DialogContent>
                                <DialogHeader>
                                  <DialogTitle>Editar Gasto de Mantenimiento</DialogTitle>
                                  <DialogDescription>Actualiza los datos del gasto.</DialogDescription>
                                </DialogHeader>
                                <form onSubmit={handleSaveMaintenance} className="space-y-4">
                                  <div className="space-y-2">
                                    <Label>Descripción *</Label>
                                    <Input
                                      value={maintenanceForm.description}
                                      onChange={(e) => setMaintenanceForm({...maintenanceForm, description: e.target.value})}
                                      required
                                    />
                                  </div>
                                  <div className="grid grid-cols-2 gap-4">
                                    <div className="space-y-2">
                                      <Label>Tipo de Equipo *</Label>
                                      <Select value={maintenanceForm.equipment_type} onValueChange={(v) => setMaintenanceForm({...maintenanceForm, equipment_type: v})}>
                                        <SelectTrigger>
                                          <SelectValue placeholder="Seleccionar" />
                                        </SelectTrigger>
                                        <SelectContent>
                                          {EQUIPMENT_TYPES.map(type => (
                                            <SelectItem key={type} value={type}>{type}</SelectItem>
                                          ))}
                                        </SelectContent>
                                      </Select>
                                    </div>
                                    <div className="space-y-2">
                                      <Label>Monto (RD$) *</Label>
                                      <Input
                                        type="number"
                                        step="0.01"
                                        min="0"
                                        value={maintenanceForm.amount}
                                        onChange={(e) => setMaintenanceForm({...maintenanceForm, amount: e.target.value})}
                                        required
                                      />
                                    </div>
                                    <div className="space-y-2">
                                      <Label>Fecha *</Label>
                                      <Input
                                        type="date"
                                        value={maintenanceForm.expense_date}
                                        onChange={(e) => setMaintenanceForm({...maintenanceForm, expense_date: e.target.value})}
                                        required
                                      />
                                    </div>
                                    <div className="space-y-2">
                                      <Label>Técnico</Label>
                                      <Input
                                        value={maintenanceForm.technician}
                                        onChange={(e) => setMaintenanceForm({...maintenanceForm, technician: e.target.value})}
                                      />
                                    </div>
                                  </div>
                                  <div className="space-y-2">
                                    <Label>Notas</Label>
                                    <Textarea
                                      value={maintenanceForm.notes}
                                      onChange={(e) => setMaintenanceForm({...maintenanceForm, notes: e.target.value})}
                                    />
                                  </div>
                                  <DialogFooter>
                                    <DialogClose asChild>
                                      <Button type="button" variant="outline" onClick={resetMaintenanceForm}>Cancelar</Button>
                                    </DialogClose>
                                    <Button type="submit" className="bg-[#931A25] hover:bg-[#7A151F]">Actualizar</Button>
                                  </DialogFooter>
                                </form>
                              </DialogContent>
                            </Dialog>
                            <AlertDialog>
                              <AlertDialogTrigger asChild>
                                <Button variant="ghost" size="sm" className="text-red-600">
                                  <Trash2 className="h-4 w-4" />
                                </Button>
                              </AlertDialogTrigger>
                              <AlertDialogContent>
                                <AlertDialogHeader>
                                  <AlertDialogTitle>Eliminar gasto</AlertDialogTitle>
                                  <AlertDialogDescription>
                                    ¿Estás seguro de eliminar este gasto de mantenimiento?
                                  </AlertDialogDescription>
                                </AlertDialogHeader>
                                <AlertDialogFooter>
                                  <AlertDialogCancel>Cancelar</AlertDialogCancel>
                                  <AlertDialogAction onClick={() => handleDeleteMaintenance(exp.id)} className="bg-red-600 hover:bg-red-700">
                                    Eliminar
                                  </AlertDialogAction>
                                </AlertDialogFooter>
                              </AlertDialogContent>
                            </AlertDialog>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
              {maintenanceExpenses.length > 0 && (
                <div className="p-4 bg-slate-50 border-t flex justify-end">
                  <span className="font-semibold">Total Mantenimiento: {formatCurrency(totalMaintenance)}</span>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* GASOIL TAB */}
        <TabsContent value="gasoil">
          <Card className="border-slate-200">
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle className="flex items-center gap-2">
                <Fuel className="h-5 w-5 text-red-600" />
                Gastos de Gasoil
              </CardTitle>
              <Dialog>
                <DialogTrigger asChild>
                  <Button className="bg-[#931A25] hover:bg-[#7A151F]" data-testid="add-gasoil-btn">
                    <Plus className="h-4 w-4 mr-2" />
                    Nuevo Gasto
                  </Button>
                </DialogTrigger>
                <DialogContent>
                  <DialogHeader>
                    <DialogTitle>Registrar Gasto de Gasoil</DialogTitle>
                  </DialogHeader>
                  <form onSubmit={handleSaveGasoil} className="space-y-4">
                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label>Litros *</Label>
                        <Input
                          type="number"
                          step="0.01"
                          value={gasoilForm.liters}
                          onChange={(e) => setGasoilForm({...gasoilForm, liters: e.target.value})}
                          placeholder="Ej: 500"
                          required
                        />
                      </div>
                      <div className="space-y-2">
                        <Label>Precio por Litro (RD$) *</Label>
                        <Input
                          type="number"
                          step="0.01"
                          value={gasoilForm.price_per_liter}
                          onChange={(e) => setGasoilForm({...gasoilForm, price_per_liter: e.target.value})}
                          placeholder="Ej: 250"
                          required
                        />
                      </div>
                    </div>
                    {gasoilForm.liters && gasoilForm.price_per_liter && (
                      <div className="p-3 bg-green-50 rounded-lg">
                        <p className="text-sm text-green-800">
                          <strong>Total:</strong> {formatCurrency(parseFloat(gasoilForm.liters) * parseFloat(gasoilForm.price_per_liter))}
                        </p>
                      </div>
                    )}
                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label>Fecha *</Label>
                        <Input
                          type="date"
                          value={gasoilForm.expense_date}
                          onChange={(e) => setGasoilForm({...gasoilForm, expense_date: e.target.value})}
                          required
                        />
                      </div>
                      <div className="space-y-2">
                        <Label>Proveedor</Label>
                        <Input
                          value={gasoilForm.supplier}
                          onChange={(e) => setGasoilForm({...gasoilForm, supplier: e.target.value})}
                          placeholder="Ej: Estación Shell"
                        />
                      </div>
                    </div>
                    <div className="space-y-2">
                      <Label>No. Factura</Label>
                      <Input
                        value={gasoilForm.invoice_number}
                        onChange={(e) => setGasoilForm({...gasoilForm, invoice_number: e.target.value})}
                        placeholder="Ej: F-001234"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Notas</Label>
                      <Textarea
                        value={gasoilForm.notes}
                        onChange={(e) => setGasoilForm({...gasoilForm, notes: e.target.value})}
                        placeholder="Observaciones adicionales..."
                      />
                    </div>
                    <DialogFooter>
                      <DialogClose asChild>
                        <Button type="button" variant="outline" onClick={resetGasoilForm}>Cancelar</Button>
                      </DialogClose>
                      <Button type="submit" className="bg-[#931A25] hover:bg-[#7A151F]">Guardar</Button>
                    </DialogFooter>
                  </form>
                </DialogContent>
              </Dialog>
            </CardHeader>
            <CardContent className="p-0">
              <Table>
                <TableHeader>
                  <TableRow className="bg-slate-50">
                    <TableHead>Fecha</TableHead>
                    <TableHead className="text-right">Litros</TableHead>
                    <TableHead className="text-right">Precio/L</TableHead>
                    <TableHead className="text-right">Total</TableHead>
                    <TableHead>Proveedor</TableHead>
                    <TableHead>Factura</TableHead>
                    <TableHead className="text-center">Acciones</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {gasoilExpenses.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={7} className="text-center py-8 text-slate-500">
                        No hay gastos de gasoil registrados en este período
                      </TableCell>
                    </TableRow>
                  ) : (
                    gasoilExpenses.map((exp) => (
                      <TableRow key={exp.id}>
                        <TableCell>{formatDate(exp.expense_date)}</TableCell>
                        <TableCell className="text-right font-medium">{exp.liters.toLocaleString()} L</TableCell>
                        <TableCell className="text-right">{formatCurrency(exp.price_per_liter)}</TableCell>
                        <TableCell className="text-right font-semibold text-red-600">{formatCurrency(exp.amount)}</TableCell>
                        <TableCell>{exp.supplier || '-'}</TableCell>
                        <TableCell>{exp.invoice_number || '-'}</TableCell>
                        <TableCell className="text-center">
                          <div className="flex items-center justify-center gap-1">
                            <Dialog>
                              <DialogTrigger asChild>
                                <Button variant="ghost" size="sm" className="text-blue-600" onClick={() => handleEditGasoil(exp)}>
                                  <Edit className="h-4 w-4" />
                                </Button>
                              </DialogTrigger>
                              <DialogContent>
                                <DialogHeader>
                                  <DialogTitle>Editar Gasto de Gasoil</DialogTitle>
                                </DialogHeader>
                                <form onSubmit={handleSaveGasoil} className="space-y-4">
                                  <div className="grid grid-cols-2 gap-4">
                                    <div className="space-y-2">
                                      <Label>Litros *</Label>
                                      <Input
                                        type="number"
                                        step="0.01"
                                        value={gasoilForm.liters}
                                        onChange={(e) => setGasoilForm({...gasoilForm, liters: e.target.value})}
                                        required
                                      />
                                    </div>
                                    <div className="space-y-2">
                                      <Label>Precio por Litro (RD$) *</Label>
                                      <Input
                                        type="number"
                                        step="0.01"
                                        value={gasoilForm.price_per_liter}
                                        onChange={(e) => setGasoilForm({...gasoilForm, price_per_liter: e.target.value})}
                                        required
                                      />
                                    </div>
                                  </div>
                                  <div className="grid grid-cols-2 gap-4">
                                    <div className="space-y-2">
                                      <Label>Fecha *</Label>
                                      <Input
                                        type="date"
                                        value={gasoilForm.expense_date}
                                        onChange={(e) => setGasoilForm({...gasoilForm, expense_date: e.target.value})}
                                        required
                                      />
                                    </div>
                                    <div className="space-y-2">
                                      <Label>Proveedor</Label>
                                      <Input
                                        value={gasoilForm.supplier}
                                        onChange={(e) => setGasoilForm({...gasoilForm, supplier: e.target.value})}
                                      />
                                    </div>
                                  </div>
                                  <div className="space-y-2">
                                    <Label>No. Factura</Label>
                                    <Input
                                      value={gasoilForm.invoice_number}
                                      onChange={(e) => setGasoilForm({...gasoilForm, invoice_number: e.target.value})}
                                    />
                                  </div>
                                  <div className="space-y-2">
                                    <Label>Notas</Label>
                                    <Textarea
                                      value={gasoilForm.notes}
                                      onChange={(e) => setGasoilForm({...gasoilForm, notes: e.target.value})}
                                    />
                                  </div>
                                  <DialogFooter>
                                    <DialogClose asChild>
                                      <Button type="button" variant="outline" onClick={resetGasoilForm}>Cancelar</Button>
                                    </DialogClose>
                                    <Button type="submit" className="bg-[#931A25] hover:bg-[#7A151F]">Actualizar</Button>
                                  </DialogFooter>
                                </form>
                              </DialogContent>
                            </Dialog>
                            <AlertDialog>
                              <AlertDialogTrigger asChild>
                                <Button variant="ghost" size="sm" className="text-red-600">
                                  <Trash2 className="h-4 w-4" />
                                </Button>
                              </AlertDialogTrigger>
                              <AlertDialogContent>
                                <AlertDialogHeader>
                                  <AlertDialogTitle>Eliminar gasto</AlertDialogTitle>
                                  <AlertDialogDescription>
                                    ¿Estás seguro de eliminar este gasto de gasoil?
                                  </AlertDialogDescription>
                                </AlertDialogHeader>
                                <AlertDialogFooter>
                                  <AlertDialogCancel>Cancelar</AlertDialogCancel>
                                  <AlertDialogAction onClick={() => handleDeleteGasoil(exp.id)} className="bg-red-600 hover:bg-red-700">
                                    Eliminar
                                  </AlertDialogAction>
                                </AlertDialogFooter>
                              </AlertDialogContent>
                            </AlertDialog>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
              {gasoilExpenses.length > 0 && (
                <div className="p-4 bg-slate-50 border-t flex justify-between items-center">
                  <span className="text-sm text-slate-500">
                    Total Litros: {gasoilExpenses.reduce((sum, g) => sum + g.liters, 0).toLocaleString()} L
                  </span>
                  <span className="font-semibold">Total Gasoil: {formatCurrency(totalGasoil)}</span>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
